/**
 * The English notice for each way a setup revision can fail, and whether the
 * user may simply retry or has to recover the project first.
 */

export const SetupRevisionFailureKinds = [
  'undoFailed',
  'applyFailed',
  'applyFailedRecoveryRequired',
  'profileSaveFailed',
  'profileReadBackMismatch',
  'documentChanged',
  'readinessFailed',
] as const;

export type SetupRevisionFailureKind = (typeof SetupRevisionFailureKinds)[number];

/**
 * 'retry'  — the revision is intact, so running the same step again is safe.
 * 'locked' — the revision cannot advance until the named recovery succeeds,
 *            and the later workflow steps stay unavailable meanwhile.
 */
export type SetupRevisionRecovery = 'retry' | 'locked';

export interface SetupRevisionFailureCopy {
  kind: SetupRevisionFailureKind;
  title: string;
  detail: string;
  recovery: SetupRevisionRecovery;
  /** What the button offering the recovery says. */
  actionLabel: string;
}

/** Failures that left the project or the saved profile part-way changed. */
const LockedKinds = new Set<SetupRevisionFailureKind>([
  'applyFailedRecoveryRequired',
  'profileReadBackMismatch',
  'documentChanged',
]);

export const isSetupRevisionFailureLocked = (kind: SetupRevisionFailureKind): boolean =>
  LockedKinds.has(kind);

type FailureText = Omit<SetupRevisionFailureCopy, 'kind' | 'recovery'>;

const FailureTexts: Record<SetupRevisionFailureKind, FailureText> = {
  undoFailed: {
    title: 'Previous connector parameters could not be removed',
    detail:
      'The connector parameters from the earlier target are still in the project, so the new'
      + ' target was not applied.',
    actionLabel: 'Retry',
  },
  applyFailed: {
    title: 'Connector parameters were not created for the new target',
    detail: 'Nothing in the project was changed, so applying the target again is safe.',
    actionLabel: 'Retry',
  },
  applyFailedRecoveryRequired: {
    title: 'Connector parameters are missing after a part-finished change',
    detail:
      'The earlier parameters were removed but the new ones were not added, so this project'
      + ' currently has no connector parameters.',
    actionLabel: 'Create parameters again',
  },
  profileSaveFailed: {
    title: 'Profile was not saved',
    detail:
      'The setup was applied in Revit but the profile itself was not stored, so these changes'
      + ' are not saved yet.',
    actionLabel: 'Save again',
  },
  profileReadBackMismatch: {
    title: 'Saved profile does not match this setup',
    detail:
      'The profile read back after saving differs from the setup on screen, so it cannot be'
      + ' trusted for the later steps.',
    actionLabel: 'Review the setup',
  },
  documentChanged: {
    title: 'Active Revit project changed',
    detail:
      'This setup was checked against a different project than the one open now, so it has to'
      + ' be rechecked.',
    actionLabel: 'Recheck the project',
  },
  readinessFailed: {
    title: 'Required parameters are not available',
    detail:
       'The availability check found missing or incompatible mapped parameters. Runtime validation'
       + ' and PCF export remain unavailable until the setup is corrected.',
    actionLabel: 'Review the mapping',
  },
};

export const resolveSetupRevisionFailure = (
  kind: SetupRevisionFailureKind
): SetupRevisionFailureCopy => ({
  kind,
  ...FailureTexts[kind],
  recovery: isSetupRevisionFailureLocked(kind) ? 'locked' : 'retry',
});
